import { useEffect, useState } from 'react'
import { BlockStack, Card, InlineGrid, InlineStack, Page, Text } from '@shopify/polaris'
import { useNavigate } from 'react-router-dom'
import { fcApi }         from '../../sdk/fc.api'
import { engineApi }     from '../../sdk/engine.api'
import { biReportsApi }  from '../../sdk/bi-reports.api'
import { getShop }       from '../../sdk/shop'
import { SystemHealthPage } from '../../modules/dt-os/system-health/pages/SystemHealthPage'

type MetricEntry = {
  key:   string
  label: string
  value: string
  unit?: string
}

function fmtEgp(n: number): { value: string; unit: string } {
  return {
    value: new Intl.NumberFormat('en-US').format(Math.round(n)),
    unit:  'EGP',
  }
}
function pct(n: number)  { return { value: `${Math.round(n * 10) / 10}%`, unit: undefined } }
function num(n: number)  { return { value: new Intl.NumberFormat('en-US').format(n), unit: undefined } }

const SECTIONS = [
  {
    title:       'Analytics Platform',
    href:        '/boarding/analytics',
    description: 'KPI engine, performance tracking, and cross-workflow reports',
  },
  {
    title:       'Forecasting Engine',
    href:        '/boarding/forecasting',
    description: 'Monthly targets, current position, and decision engine',
  },
  {
    title:       'Financial Engine',
    href:        '/boarding/financial',
    description: 'Real profitability from settlement data, COGS, and marketing costs',
  },
  {
    title:       'Marketing',
    href:        '/boarding/marketing',
    description: 'Marketing workflow management and campaign tracking',
  },
  {
    title:       'History',
    href:        '/boarding/history',
    description: 'Agent activity, attendance, leaderboards, and anomaly flags across all workflows',
  },
]

export function BoardingOverviewPage() {
  const navigate      = useNavigate()
  const shop          = getShop() ?? ''
  const [metrics,  setMetrics]  = useState<MetricEntry[] | null>(null)
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    if (!shop) return
    Promise.all([
      fcApi.getDashboard(shop).catch(() => null),
      engineApi.metrics(shop, 'customer_service').catch(() => null),
      biReportsApi.getOrders(shop).catch(() => null),
    ]).then(([fc, cs, ordersReport]) => {
      const revenue      = fc?.revenue.net ?? 0
      const ordersCount  = (ordersReport?.summary?.['totalOrders'] as number | null) ?? fc?.revenue.ordersCount ?? 0
      const aov          = ordersCount > 0 ? revenue / ordersCount : 0
      const delivRate    = fc?.delivery.deliveryRate ?? 0
      const returnCount  = fc?.returns.count ?? 0
      const returnRate   = ordersCount > 0 ? (returnCount / ordersCount) * 100 : 0
      const resolved     = cs?.closedToday?.byOutcome?.['cs_completed'] ?? 0
      const open         = cs?.active ?? 0
      const csTotal      = resolved + open
      const csResRate    = csTotal > 0 ? (resolved / csTotal) * 100 : 0

      const dash: MetricEntry[] = [
        { key: 'revenue',      label: 'Revenue',       ...(revenue > 0      ? fmtEgp(revenue)  : { value: '—', unit: undefined }) },
        { key: 'orders',       label: 'Orders',        ...(ordersCount > 0  ? num(ordersCount)  : { value: '—', unit: undefined }) },
        { key: 'aov',          label: 'AOV',           ...(aov > 0          ? fmtEgp(aov)      : { value: '—', unit: undefined }) },
        { key: 'deliveryRate', label: 'Delivery Rate', ...(delivRate > 0    ? pct(delivRate)   : { value: '—', unit: undefined }) },
        { key: 'returnRate',   label: 'Return Rate',   ...(returnCount > 0  ? pct(returnRate)  : { value: '—', unit: undefined }) },
        { key: 'csResolution', label: 'CS Resolution', ...(csTotal > 0      ? pct(csResRate)   : { value: '—', unit: undefined }) },
      ]
      setMetrics(dash)
    }).finally(() => setLoading(false))
  }, [shop])

  return (
    <Page title="Boarding" subtitle="Executive overview">
      <BlockStack gap="600">

        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Summary</Text>
          <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <Card key={i}>
                    <BlockStack gap="100">
                      <div style={{ height: 14, width: 70, background: 'var(--p-color-bg-fill-secondary)', borderRadius: 4 }} />
                      <div style={{ height: 32, width: 100, background: 'var(--p-color-bg-fill-secondary)', borderRadius: 4 }} />
                    </BlockStack>
                  </Card>
                ))
              : metrics?.map((m) => (
                  <Card key={m.key}>
                    <BlockStack gap="050">
                      <Text as="p" variant="bodySm" tone="subdued">{m.label}</Text>
                      <Text as="p" variant="headingXl" tone={m.value === '—' ? 'subdued' : undefined}>
                        {m.value}
                      </Text>
                      {m.unit && (
                        <Text as="p" variant="bodySm" tone="subdued">{m.unit}</Text>
                      )}
                    </BlockStack>
                  </Card>
                ))
            }
          </InlineGrid>
        </BlockStack>

        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Sections</Text>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
            {SECTIONS.map((s) => (
              <Card key={s.href}>
                <BlockStack gap="300">
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingSm">{s.title}</Text>
                    <Text as="p" variant="bodySm" tone="subdued">{s.description}</Text>
                  </BlockStack>
                  <InlineStack align="end">
                    <button
                      onClick={() => navigate(s.href)}
                      style={{
                        padding:      '6px 16px',
                        borderRadius: '8px',
                        border:       '1px solid var(--p-color-border)',
                        background:   'transparent',
                        color:        'var(--p-color-text)',
                        cursor:       'pointer',
                        fontSize:     '14px',
                      }}
                    >
                      Open
                    </button>
                  </InlineStack>
                </BlockStack>
              </Card>
            ))}
          </InlineGrid>
        </BlockStack>

        <SystemHealthPage mode="wizard" />

      </BlockStack>
    </Page>
  )
}
